/**
 * Frontend product docs catalog (developer integration guides).
 * Markdown bodies live under public/docs/<slug>/index.md and are served statically
 * so raw export URLs work without the Go API.
 */

export const DOCS_INDEX_MD = "index.md";

/**
 * True when the browser path should load the SPA docs reader (not a static *.md file).
 * Used by Vite dev/preview middleware and unit tests so public/docs/ directories
 * never steal /docs or /docs/<slug> deep links.
 */
export function isDocsSpaPath(pathname: string): boolean {
  const p = (pathname.split("?")[0] ?? "").split("#")[0] ?? "";
  if (p !== "/docs" && !p.startsWith("/docs/")) return false;
  if (p.toLowerCase().endsWith(".md")) return false;
  return true;
}

export type DocEntry = {
  /** URL slug segment under /docs (e.g. "getting-started"). */
  slug: string;
  title: string;
  summary: string;
  /** Sort order (lower first). */
  order: number;
};

/** Canonical product doc pages — keep in sync with public/docs/* /index.md files. */
export const DOC_ENTRIES: readonly DocEntry[] = [
  {
    slug: "getting-started",
    title: "Getting started",
    summary: "Create a project, grab your public key, and call TinyBase from your app.",
    order: 10,
  },
  {
    slug: "javascript-client",
    title: "JavaScript client",
    summary: "Install @knotree/client and configure url + projectKey.",
    order: 20,
  },
  {
    slug: "api-keys",
    title: "API keys",
    summary: "Public tb_pk_* keys for browsers vs service keys that stay on servers.",
    order: 30,
  },
  {
    slug: "auth-v2-popup",
    title: "Auth v2: popup, email OTP, and Google",
    summary:
      "The current app-user authentication flow for frontend applications.",
    order: 35,
  },
  {
    slug: "authentication",
    title: "Authentication",
    summary: "Direct app-user sign-up, sign-in, and session management.",
    order: 40,
  },
  {
    slug: "session-management",
    title: "Session management",
    summary:
      "How app-user sessions persist, refresh, and stay alive across tabs and reloads.",
    order: 42,
  },
  {
    slug: "hosted-auth-sso",
    title: "Hosted Auth SSO",
    summary:
      "Legacy/standby Application OAuth with hosted consent, OAuth, and PKCE.",
    order: 45,
  },
  {
    slug: "data-api",
    title: "Data API",
    summary: "Query tables with from(), filters, inserts, and RPC.",
    order: 50,
  },
  {
    slug: "edge-functions",
    title: "Edge Functions",
    summary: "Deploy project functions and invoke them from the client.",
    order: 60,
  },
  {
    slug: "cors-and-security",
    title: "CORS and security",
    summary: "Browser origins, RLS ownership columns, and what never ships to the client.",
    order: 70,
  },
] as const;

/** Absolute SPA path for a doc page (HTML reader). */
export function docsPagePath(slug: string): string {
  const s = slug.replace(/^\/+|\/+$/g, "");
  return s ? `/docs/${s}` : "/docs";
}

/**
 * Static raw markdown path for a doc (or the catalog).
 * Convention: every page exports at /docs/<slug>/index.md; catalog at /docs/index.md.
 */
export function docsRawMarkdownPath(slug?: string | null): string {
  const s = (slug ?? "").replace(/^\/+|\/+$/g, "");
  if (!s || s === "index" || s === DOCS_INDEX_MD) {
    return `/docs/${DOCS_INDEX_MD}`;
  }
  // Accept accidental ".../index.md" in slug
  const cleaned = s.replace(/\/index\.md$/i, "").replace(/\.md$/i, "");
  return `/docs/${cleaned}/${DOCS_INDEX_MD}`;
}

/**
 * Resolve a browser pathname under /docs to { kind, slug }.
 * - /docs or /docs/ → catalog
 * - /docs/foo or /docs/foo/ → page foo
 * - /docs/foo/index.md or /docs/index.md → raw (callers may still load via fetch)
 */
export function resolveDocsPath(pathname: string): {
  kind: "catalog" | "page" | "raw";
  slug: string;
  rawPath: string;
} {
  const raw = (pathname.split("?")[0] ?? "/").trim() || "/";
  let p = raw.length > 1 && raw.endsWith("/") ? raw.slice(0, -1) : raw;
  if (!p.startsWith("/docs")) {
    return { kind: "catalog", slug: "", rawPath: docsRawMarkdownPath() };
  }
  let rest = p.slice("/docs".length).replace(/^\/+/, "");
  if (!rest) {
    return { kind: "catalog", slug: "", rawPath: docsRawMarkdownPath() };
  }
  const lower = rest.toLowerCase();
  if (lower === "index.md" || lower === DOCS_INDEX_MD) {
    return { kind: "raw", slug: "", rawPath: docsRawMarkdownPath() };
  }
  if (lower.endsWith("/index.md") || lower.endsWith(".md")) {
    const withoutMd = rest.replace(/\/index\.md$/i, "").replace(/\.md$/i, "");
    return {
      kind: "raw",
      slug: withoutMd,
      rawPath: docsRawMarkdownPath(withoutMd),
    };
  }
  return {
    kind: "page",
    slug: rest,
    rawPath: docsRawMarkdownPath(rest),
  };
}

export function findDocEntry(slug: string): DocEntry | undefined {
  const s = slug.replace(/^\/+|\/+$/g, "");
  return DOC_ENTRIES.find((e) => e.slug === s);
}

/** Build the catalog markdown body (also mirrored under public/docs/index.md). */
export function buildCatalogMarkdown(): string {
  const lines = [
    "# TinyBase developer docs",
    "",
    "Integration guides for embedding TinyBase auth, data, and APIs in your applications.",
    "These pages are product docs for app developers — not internal harness or repo ops notes.",
    "",
    "Each guide is available as HTML in the docs UI and as raw markdown at `/docs/<slug>/index.md`.",
    "",
    "## Guides",
    "",
  ];
  for (const e of [...DOC_ENTRIES].sort((a, b) => a.order - b.order)) {
    lines.push(
      `- [${e.title}](/docs/${e.slug}) — ${e.summary} ([raw](/docs/${e.slug}/index.md))`,
    );
  }
  lines.push("");
  return lines.join("\n");
}

/**
 * Copy text to the clipboard when available.
 * Returns true when writeText resolved successfully.
 */
export async function copyTextToClipboard(
  text: string,
  clipboard: Pick<Clipboard, "writeText"> | null | undefined = globalThis.navigator
    ?.clipboard,
): Promise<boolean> {
  if (!clipboard?.writeText) {
    return false;
  }
  try {
    await clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Fetch raw markdown for a docs slug (catalog when slug empty). */
export async function fetchDocsMarkdown(
  slug: string | null | undefined,
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<{ ok: true; markdown: string; path: string } | { ok: false; error: string; path: string }> {
  const path = docsRawMarkdownPath(slug);
  try {
    const res = await fetchImpl(path, {
      headers: { Accept: "text/markdown, text/plain, */*" },
    });
    if (!res.ok) {
      return { ok: false, error: `HTTP ${res.status}`, path };
    }
    const markdown = await res.text();
    return { ok: true, markdown, path };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "fetch failed",
      path,
    };
  }
}
